/**
 * B2B Business Landing Page
 * Structural mirror of the B2C HomePage:
 *   Hero  →  same rounded card shape, nav INSIDE hero, wave at bottom
 *   How It Works  →  dark section (mirrors YouTube section)
 *   Feature Tabs  →  same ComparisonCards layout
 *   Platform Center  →  same JobSeekerCenter orbital layout
 *   Final CTA  →  same FinalCTASimple layout
 */
import { motion, AnimatePresence } from 'framer-motion';
import {
  Moon, Sun, ArrowRight, Menu, X, ArrowUpRight, ImageIcon,
  Briefcase, Users, BarChart3, Video, FileSearch, Settings2,
  Zap, CheckCircle, GitCompare, Target, AlertCircle, Search,
  Bell, CalendarDays, Mail, Building2, User, Phone,
  ChevronRight, Check, UserPlus, LayoutDashboard, Loader2,
  FileText, History, Sparkles,
} from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { GGJLogoFull } from '../components/Brand/GGJLogo';
import { getB2BAppUrl } from '../config/b2bAppUrl';
import { useTheme } from '../contexts/ThemeContext';
import { SEO } from '../shared/components/SEO';

const BRAND_GRADIENT = 'var(--brand-cta-bg)';

interface BusinessPageProps {
  onNavigate?: (view: string) => void;
}

/* ═══════════════════════════════════════════════
   ILLUSTRATION PLACEHOLDER
   Drop-in placeholder for client illustrations
═══════════════════════════════════════════════ */
function IllustrationPlaceholder({
  label = 'Illustration',
  sublabel = 'Replace with your illustration',
  height = 'h-56',
  icon,
  className = '',
}: {
  label?: string;
  sublabel?: string;
  height?: string;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`${height} ${className} w-full rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 flex flex-col items-center justify-center gap-3`}>
      <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
        {icon ?? <ImageIcon className="w-6 h-6 text-violet-400 dark:text-violet-500" />}
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-gray-400 dark:text-gray-500">{label}</p>
        <p className="text-xs text-gray-300 dark:text-gray-600 mt-0.5">{sublabel}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   HERO RIGHT — image-3-style floating mockup
   Placeholder cards like ReadymadeUI reference
═══════════════════════════════════════════════ */
function HeroMockupPlaceholder() {
  return (
    <div className="relative w-full h-[340px] flex items-center justify-center">
      {/* Glow circle */}
      <div
        className="absolute w-[72%] aspect-square rounded-full pointer-events-none"
        style={{ background: 'rgba(255,255,255,0.10)', filter: 'blur(40px)' }}
        aria-hidden
      />

      {/* Main dashboard placeholder card */}
      <div className="relative z-10 w-[56%] aspect-[4/3] rounded-2xl shadow-2xl flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/40"
        style={{ background: 'rgba(255,255,255,0.13)', backdropFilter: 'blur(12px)' }}>
        <LayoutDashboard className="w-9 h-9 text-white/60" />
        <div className="text-center px-3">
          <p className="text-xs font-semibold text-white/80">Dashboard Screenshot</p>
          <p className="text-[10px] text-white/50 mt-0.5">Add your app screenshot here</p>
        </div>
      </div>

      {/* Top-right floating card */}
      <div className="absolute top-4 right-4 z-20 w-[40%] aspect-video rounded-xl shadow-xl flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/35"
        style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(12px)' }}>
        <Video className="w-5 h-5 text-white/70" />
        <span className="text-[10px] text-white/70 font-medium">AI Interview View</span>
      </div>

      {/* Bottom-left floating card */}
      <div className="absolute bottom-4 left-4 z-20 w-[37%] aspect-video rounded-xl shadow-xl flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/35"
        style={{ background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(12px)' }}>
        <BarChart3 className="w-5 h-5 text-white/70" />
        <span className="text-[10px] text-white/70 font-medium">Analytics</span>
      </div>

      {/* Social proof row (image 3 style) */}
      <div className="absolute bottom-[-16px] left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 py-2 rounded-full shadow-xl"
        style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.3)' }}>
        <div className="flex -space-x-2">
          {['A', 'B', 'C'].map(l => (
            <div key={l} className="w-6 h-6 rounded-full border-2 border-white/60 flex items-center justify-center text-[9px] font-bold text-white"
              style={{ background: BRAND_GRADIENT }}>
              {l}
            </div>
          ))}
        </div>
        <span className="text-[10px] font-semibold text-white whitespace-nowrap">500+ companies trust us</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FEATURE TABS — 4 simulations (like ComparisonCards)
═══════════════════════════════════════════════ */

// Tab 1: Screen — inbox chaos → AI ranked list
function ScreenSimulation() {
  const [phase, setPhase] = useState<'inbox' | 'scanning' | 'ranked'>('inbox');
  useEffect(() => {
    if (phase === 'inbox') { const t = setTimeout(() => setPhase('scanning'), 2000); return () => clearTimeout(t); }
    if (phase === 'scanning') { const t = setTimeout(() => setPhase('ranked'), 2000); return () => clearTimeout(t); }
    if (phase === 'ranked') { const t = setTimeout(() => setPhase('inbox'), 3200); return () => clearTimeout(t); }
  }, [phase]);

  const emails = ['CV_JohnSmith.pdf', 'Application_Sarah.docx', 'Resume_Final_v3.pdf', 'CV_no_name.pdf', 'Priya_Kumar_resume.pdf', 'application_2024.pdf'];
  const ranked = [
    { name: 'Maya R.', score: 94, tag: 'Top Match' },
    { name: 'Carlos M.', score: 88, tag: 'Strong Fit' },
    { name: 'Priya K.', score: 76, tag: 'Good Fit' },
  ];

  return (
    <div className="rounded-2xl rounded-t-[1.25rem] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-hidden shadow-xl w-full max-w-2xl mx-auto min-h-[320px] flex flex-col">
      <div className="h-1 w-full shrink-0" style={{ background: BRAND_GRADIENT }} />
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <FileSearch className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {phase === 'inbox' ? 'Inbox — 84 unread applications' : phase === 'scanning' ? 'AI scanning CVs…' : 'AI Ranked Shortlist'}
          </span>
        </div>
        {phase === 'ranked' && <span className="text-[10px] text-green-600 dark:text-green-400 font-semibold">Done in 4s</span>}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-3">
        {phase === 'inbox' && (
          <motion.div key="inbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-1.5">
            {emails.map((f, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
                <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{f}</span>
              </div>
            ))}
            <p className="text-center text-[10px] text-red-500 font-medium pt-1">+ 78 more emails to review manually…</p>
          </motion.div>
        )}
        {phase === 'scanning' && (
          <motion.div key="scanning" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center flex-1 gap-4 py-6">
            <div className="relative w-14 h-14">
              <div className="absolute inset-0 rounded-full border-4 border-violet-200 dark:border-violet-800" />
              <div className="absolute inset-0 rounded-full border-4 border-t-violet-600 dark:border-t-violet-400 animate-spin" />
              <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">AI reading all 84 CVs…</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Parsing skills, experience, seniority</p>
            </div>
          </motion.div>
        )}
        {phase === 'ranked' && (
          <motion.div key="ranked" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
            {ranked.map((r, i) => (
              <div key={r.name} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700">
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: BRAND_GRADIENT }}>{i + 1}</span>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">{r.name}</p>
                  <span className="text-[10px] text-violet-600 dark:text-violet-400 font-medium">{r.tag}</span>
                </div>
                <span className="text-sm font-bold text-green-600 dark:text-green-400">{r.score}%</span>
              </div>
            ))}
            <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 pt-1">81 others ranked below threshold</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// Tab 2: Interview — parallel launches
function InterviewSimulation() {
  const [highlight, setHighlight] = useState(0);
  const [allLaunched, setAllLaunched] = useState(false);
  const reset = useCallback(() => { setHighlight(0); setAllLaunched(false); }, []);
  useEffect(() => {
    const t1 = setTimeout(() => setHighlight(1), 1800);
    const t2 = setTimeout(() => setHighlight(2), 3000);
    const t3 = setTimeout(() => setHighlight(3), 4200);
    const t4 = setTimeout(() => setAllLaunched(true), 5000);
    const t5 = setTimeout(reset, 7500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, [reset]);
  const rows = [
    { name: 'Maya R.', role: 'Senior React Dev', time: '09:00' },
    { name: 'Carlos M.', role: 'Full-Stack Engineer', time: '09:05' },
    { name: 'Priya K.', role: 'Frontend Dev', time: '09:10' },
  ];
  return (
    <div className="rounded-2xl rounded-t-[1.25rem] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-hidden shadow-xl w-full max-w-2xl mx-auto min-h-[320px] flex flex-col">
      <div className="h-1 w-full shrink-0" style={{ background: BRAND_GRADIENT }} />
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2"><Video className="w-4 h-4 text-gray-500 dark:text-gray-400" /><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">AI Interviews</span></div>
        <span className="text-[10px] text-gray-500 dark:text-gray-400">3 selected</span>
      </div>
      <div className="p-3 flex-1 min-h-0 flex flex-col">
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden flex-1">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 bg-gray-50 dark:bg-gray-900/50">
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span className="text-[10px] text-gray-400">Shortlisted candidates</span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((r, i) => {
              const isActive = highlight > i;
              return (
                <motion.div key={r.name} className={`flex items-center gap-3 px-3 py-3 ${isActive ? 'bg-green-50/70 dark:bg-green-900/15' : ''}`}
                  animate={isActive ? { scale: [1, 1.01, 1] } : {}} transition={{ duration: 0.3 }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: BRAND_GRADIENT }}>{r.name[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{r.name}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{r.role}</p>
                  </div>
                  <div className="shrink-0">
                    {isActive ? (
                      <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600 dark:text-green-400">
                        <CheckCircle className="w-3.5 h-3.5" />Launched {r.time}
                      </span>
                    ) : i === highlight ? (
                      <Loader2 className="w-3.5 h-3.5 text-violet-500 animate-spin" />
                    ) : (
                      <span className="text-[10px] text-gray-400">Queued</span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
        <AnimatePresence>
          {allLaunched && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-3 rounded-xl px-4 py-2.5 flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 shrink-0">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
              <span className="text-xs font-medium text-green-800 dark:text-green-200">3 parallel AI interviews running — no recruiter needed</span>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-800 shrink-0 flex items-center gap-4 text-[10px] text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-700 dark:text-gray-300">Today:</span>
          <span>Maya · 09:00</span><span>Carlos · 09:05</span><span>Priya · 09:10</span>
        </div>
      </div>
    </div>
  );
}

// Tab 3: Pipeline — team collaboration
const BAR_HEIGHTS = [62, 80, 45, 90, 70, 85, 75];
const TEAM_MEMBERS = [
  { initial: 'You', isYou: true }, { initial: 'A', isYou: false },
  { initial: 'K', isYou: false }, { initial: '+', isYou: false, isAdd: true },
];
function PipelineSimulation() {
  const [highlightBar, setHighlightBar] = useState(0);
  useEffect(() => { const tid = setInterval(() => setHighlightBar(n => (n + 1) % 7), 700); return () => clearInterval(tid); }, []);
  const stages = [
    { label: 'Applied', count: 247, color: 'bg-gray-400' }, { label: 'Screened', count: 168, color: 'bg-blue-400' },
    { label: 'Interview', count: 42, color: 'bg-violet-500' }, { label: 'Offer', count: 8, color: 'bg-green-500' },
  ];
  return (
    <div className="rounded-2xl rounded-t-[1.25rem] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-hidden shadow-xl w-full max-w-2xl mx-auto min-h-[320px] flex flex-col">
      <div className="h-1 w-full shrink-0" style={{ background: BRAND_GRADIENT }} />
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-600 flex items-center gap-2 shrink-0">
        <LayoutDashboard className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Pipeline — Senior React Dev</span>
      </div>
      <div className="p-3 space-y-3 flex-1 min-h-0 flex flex-col overflow-y-auto">
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-2.5 bg-gray-50/50 dark:bg-gray-900/30 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />Hiring Team</span>
            <button type="button" className="text-[10px] font-medium px-2 py-1 rounded-lg bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">Invite</button>
          </div>
          <div className="flex items-center gap-1">
            {TEAM_MEMBERS.map(m => (
              <div key={m.initial} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 ${m.isAdd ? 'bg-gray-200 dark:bg-gray-700 border-dashed border-gray-400 dark:border-gray-500 text-gray-500' : m.isYou ? 'bg-violet-100 dark:bg-violet-900/40 border-violet-300 dark:border-violet-600 text-violet-700 dark:text-violet-300' : 'bg-gray-200 dark:bg-gray-600 border-white dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>
                {m.isAdd ? <UserPlus className="w-3.5 h-3.5" /> : m.initial}
              </div>
            ))}
            <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-1">Frontend Hiring 2024</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 shrink-0">
          <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-2 bg-white dark:bg-gray-800">
            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 block mb-2">Stage funnel</span>
            <div className="space-y-1.5">
              {stages.map(s => (
                <div key={s.label} className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${s.color} shrink-0`} />
                  <span className="text-[9px] text-gray-600 dark:text-gray-400 flex-1">{s.label}</span>
                  <span className="text-[9px] font-bold text-gray-900 dark:text-white">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-2 bg-gray-50/50 dark:bg-gray-900/30 shrink-0">
            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Weekly activity</span>
            <div className="h-10 flex items-end gap-0.5">
              {BAR_HEIGHTS.map((h, i) => (
                <motion.div key={i} className="flex-1 rounded-t min-w-0" style={{ height: `${h}%`, background: BRAND_GRADIENT }}
                  animate={{ opacity: highlightBar === i ? 1 : 0.55 }} transition={{ duration: 0.3 }} />
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-2 bg-amber-50/50 dark:bg-amber-900/20 shrink-0">
          <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1 mb-1"><Bell className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />Team notes</span>
          <div className="text-[10px] text-gray-600 dark:text-gray-400 space-y-0.5">
            <div>A → "Maya's system design was excellent"</div>
            <div>K → "Carlos: schedule final round"</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Tab 4: Insights — comparison table
const COMPARE_FEATURES = ['AI candidate screening', 'Automated video interviews', 'ATS pipeline tracking', 'Bulk interview launch', 'Team collaboration', 'Analytics & reports'];
function InsightsSimulation() {
  return (
    <div className="rounded-2xl rounded-t-[1.25rem] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-hidden shadow-xl w-full max-w-2xl mx-auto min-h-[320px] flex flex-col">
      <div className="h-1 w-full shrink-0" style={{ background: BRAND_GRADIENT }} />
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-600 flex items-center gap-2 shrink-0">
        <GitCompare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why GoGrabJob for Business wins</span>
      </div>
      <div className="p-4 flex-1 min-h-0 flex flex-col">
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden shrink-0">
          <div className="grid grid-cols-3 text-[10px] font-semibold bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-600">
            <div className="px-3 py-2.5 text-gray-600 dark:text-gray-400">Feature</div>
            <div className="px-3 py-2.5 text-center text-gray-600 dark:text-gray-400">Legacy ATS</div>
            <div className="px-3 py-2.5 text-center text-violet-700 dark:text-violet-300 bg-violet-50/50 dark:bg-violet-900/20">GoGrabJob</div>
          </div>
          {COMPARE_FEATURES.map(feat => (
            <div key={feat} className="grid grid-cols-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
              <div className="px-3 py-2.5 text-xs text-gray-800 dark:text-gray-200">{feat}</div>
              <div className="px-3 py-2.5 flex justify-center items-center"><X className="w-4 h-4 text-red-500 dark:text-red-400" /></div>
              <div className="px-3 py-2.5 flex justify-center items-center bg-violet-50/30 dark:bg-violet-900/10"><CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400" /></div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[10px] text-gray-600 dark:text-gray-400 shrink-0">Legacy ATS tools require weeks of setup and lack AI automation. GoGrabJob is live in minutes.</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FEATURE TABS SECTION (mirrors ComparisonCards)
═══════════════════════════════════════════════ */
const B2B_TABS = [
  { id: 'screen'    as const, label: 'Screen',    icon: AlertCircle },
  { id: 'interview' as const, label: 'Interview', icon: Video       },
  { id: 'pipeline'  as const, label: 'Pipeline',  icon: Target      },
  { id: 'insights'  as const, label: 'Insights',  icon: GitCompare  },
];
const TAB_COLORS_LIGHT: Record<string, { bg: string; rightGradient: string }> = {
  screen:    { bg: '#f8f6ff', rightGradient: 'linear-gradient(135deg, #f0edff 0%, #f8f6ff 100%)' },
  interview: { bg: '#fdf2f8', rightGradient: 'linear-gradient(135deg, #fce7f3 0%, #fdf2f8 100%)' },
  pipeline:  { bg: '#eff6ff', rightGradient: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)' },
  insights:  { bg: '#fff7ed', rightGradient: 'linear-gradient(135deg, #ffedd5 0%, #fff7ed 100%)' },
};
const TAB_COLORS_DARK: Record<string, { bg: string; rightGradient: string }> = {
  screen: { bg: '#0d0d0e', rightGradient: 'linear-gradient(135deg, #111113 0%, #0d0d0e 100%)' },
  interview: { bg: '#0d0d0e', rightGradient: 'linear-gradient(135deg, #111113 0%, #0d0d0e 100%)' },
  pipeline: { bg: '#0d0d0e', rightGradient: 'linear-gradient(135deg, #111113 0%, #0d0d0e 100%)' },
  insights: { bg: '#0d0d0e', rightGradient: 'linear-gradient(135deg, #111113 0%, #0d0d0e 100%)' },
};
const TAB_COPY: Record<string, { title: string; desc: string }> = {
  screen:    { title: 'Stop drowning in CVs',              desc: 'Recruiters waste 23 hours per week manually screening applications. Our AI reads every CV, scores candidates against your requirements, and delivers a ranked shortlist in seconds — not days.' },
  interview: { title: 'Interview at scale, automatically', desc: 'Launch structured AI video interviews for your entire shortlist simultaneously. Our AI conducts the conversation, transcribes responses, and scores each candidate while your team focuses on other work.' },
  pipeline:  { title: 'Your whole team, one pipeline',     desc: 'Track every candidate from first application to final offer in a shared pipeline. Leave notes, assign reviewers, and move candidates between stages — with your entire hiring team in sync.' },
  insights:  { title: 'Built for the way modern teams hire', desc: 'Legacy ATS tools were built before AI. GoGrabJob combines a full ATS with cutting-edge AI screening and interviewing — live in minutes, not weeks of setup.' },
};

function B2BFeatureTabs({ b2bLoginUrl }: { b2bLoginUrl: string }) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<(typeof B2B_TABS)[number]['id']>('screen');
  const TAB_COLORS = theme === 'dark' ? TAB_COLORS_DARK : TAB_COLORS_LIGHT;
  const { bg: tabBodyBg, rightGradient } = TAB_COLORS[activeTab];
  const copy = TAB_COPY[activeTab];

  return (
    <section id="features" className="py-16 md:py-24 bg-gray-100/80 dark:bg-gray-900 overflow-visible scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3">The complete AI hiring platform</h2>
          <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">From first application to final offer — every step automated, every insight surfaced.</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="rounded-[2rem] md:rounded-[2.5rem] shadow-xl overflow-visible border border-gray-200/80 dark:border-gray-700/50">
          {/* Tabs */}
          <div className="border-b border-gray-200/80 dark:border-gray-700/50 bg-white/60 dark:bg-gray-900/40 rounded-t-[2rem] md:rounded-t-[2.5rem]">
            <div className="flex flex-wrap gap-0 px-8 md:px-12 lg:px-16 pt-6 md:pt-8 pb-0 overflow-x-auto min-w-0">
              {B2B_TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-colors -mb-px ${isActive ? 'text-[#6C47FF] dark:text-purple-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    {tab.label}
                    {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ background: theme === 'dark' ? 'var(--vscode-text-secondary)' : BRAND_GRADIENT }} aria-hidden />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab body */}
          <div className="p-8 md:p-10 lg:p-14 min-h-[400px] md:min-h-[440px] transition-colors duration-300 text-gray-900" role="tabpanel" style={{ backgroundColor: tabBodyBg }}>
            <div className="flex flex-col md:flex-row md:justify-between w-full gap-10 lg:gap-14 items-stretch md:items-center h-full min-h-0">
              <div className="order-2 md:order-1 w-full md:w-[33.333%] md:max-w-[calc(100%/3)] flex-shrink-0 min-h-0 overflow-y-auto">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{copy.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{copy.desc}</p>
              </div>
              <div className="order-1 md:order-2 w-full md:w-[66.666%] md:min-w-0 relative flex items-center justify-end h-[340px] md:h-[380px] rounded-3xl overflow-visible md:-mr-6 lg:-mr-12 xl:-mr-20 shrink-0"
                style={{ background: rightGradient }}>
                <div className="absolute inset-0 rounded-3xl opacity-70 dark:opacity-50 pointer-events-none"
                  style={{ backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.06) 1px, transparent 1px)', backgroundSize: '20px 20px' }} aria-hidden />
                <div className="relative z-10 w-full max-w-2xl ml-auto flex items-center justify-center md:justify-end p-4 pr-0 md:pr-4 h-[320px] overflow-hidden">
                  {activeTab === 'screen' && <motion.div key="screen" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl"><ScreenSimulation /></motion.div>}
                  {activeTab === 'interview' && <motion.div key="interview" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl"><InterviewSimulation /></motion.div>}
                  {activeTab === 'pipeline' && <motion.div key="pipeline" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl"><PipelineSimulation /></motion.div>}
                  {activeTab === 'insights' && <motion.div key="insights" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl"><InsightsSimulation /></motion.div>}
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="px-8 md:px-12 lg:px-16 pb-10 md:pb-12 pt-2 text-center" style={{ backgroundColor: tabBodyBg }}>
            <a href={b2bLoginUrl} target="_blank" rel="noopener noreferrer"
              className="inline-block px-8 py-4 text-white font-semibold rounded-2xl shadow-lg hover:opacity-90 transition-opacity"
              style={{ background: BRAND_GRADIENT }}>
              Start hiring smarter — free
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   PLATFORM ORBITAL (mirrors JobSeekerCenter)
═══════════════════════════════════════════════ */
const RADIUS_PCT = 38;
const B2B_OFFERINGS = [
  { icon: FileSearch, title: 'AI Screening',   text: 'Auto-rank all applicants instantly',       iconBg: 'bg-purple-100 dark:bg-purple-900/40', iconColor: 'text-purple-600 dark:text-purple-400' },
  { icon: Video,      title: 'AI Interviews',  text: 'Launch parallel video interviews',          iconBg: 'bg-blue-100 dark:bg-blue-900/40',     iconColor: 'text-blue-600 dark:text-blue-400'   },
  { icon: Target,     title: 'ATS Pipeline',   text: 'Track every candidate stage',               iconBg: 'bg-emerald-100 dark:bg-emerald-900/40', iconColor: 'text-emerald-600 dark:text-emerald-400' },
  { icon: BarChart3,  title: 'Analytics',      text: 'Time-to-hire & funnel insights',            iconBg: 'bg-violet-100 dark:bg-violet-900/40', iconColor: 'text-violet-600 dark:text-violet-400' },
  { icon: Zap,        title: 'Bulk Actions',   text: 'Invite & screen in one click',              iconBg: 'bg-amber-100 dark:bg-amber-900/40',   iconColor: 'text-amber-600 dark:text-amber-400' },
  { icon: FileText,   title: 'Resume Parser',  text: 'Extract skills & experience',               iconBg: 'bg-rose-100 dark:bg-rose-900/40',     iconColor: 'text-rose-600 dark:text-rose-400'   },
  { icon: Users,      title: 'Team Collab',    text: 'Share scorecards & feedback',               iconBg: 'bg-teal-100 dark:bg-teal-900/40',     iconColor: 'text-teal-600 dark:text-teal-400'   },
  { icon: Bell,       title: 'Smart Alerts',   text: 'Never miss a hot candidate',                iconBg: 'bg-orange-100 dark:bg-orange-900/40', iconColor: 'text-orange-600 dark:text-orange-400' },
  { icon: History,    title: 'Audit Trail',    text: 'Full history of every decision',            iconBg: 'bg-indigo-100 dark:bg-indigo-900/40', iconColor: 'text-indigo-600 dark:text-indigo-400' },
  { icon: Settings2,  title: 'Custom Flows',   text: 'Configurable interview rubrics',            iconBg: 'bg-cyan-100 dark:bg-cyan-900/40',     iconColor: 'text-cyan-600 dark:text-cyan-400'   },
];
const N = B2B_OFFERINGS.length;
const RING_POINTS = Array.from({ length: N }, (_, i) => {
  const a = ((-90 + (i * 360) / N) * Math.PI) / 180;
  return { x: 50 + RADIUS_PCT * Math.cos(a), y: 50 + RADIUS_PCT * Math.sin(a) };
});
const ZIGZAG_ORDER = [0, 5, 1, 6, 2, 7, 3, 8, 4, 9];
const ZIGZAG_POINTS = ZIGZAG_ORDER.map(i => RING_POINTS[i]);
const RING_PATH = `M ${ZIGZAG_POINTS.map(p => `${p.x} ${p.y}`).join(' L ')} Z`;
function getArrowPos(progress: number) {
  const seg = (progress * N) % N;
  const i = Math.floor(seg) % N;
  const t = seg - Math.floor(seg);
  const p0 = ZIGZAG_POINTS[i]; const p1 = ZIGZAG_POINTS[(i + 1) % N];
  return { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t, angleDeg: (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI };
}

function B2BPlatformCenter({ onScheduleDemo }: { onScheduleDemo: () => void }) {
  const [arrowProgress, setArrowProgress] = useState(0);
  useEffect(() => {
    const start = Date.now(); let rafId: number;
    const tick = () => { setArrowProgress(((Date.now() - start) / 1000 / 18) % 1); rafId = requestAnimationFrame(tick); };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);
  const ap = getArrowPos(arrowProgress);

  return (
    <section id="platform" className="py-16 md:py-24 bg-white dark:bg-gray-900 relative overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 pointer-events-none opacity-50 text-gray-300 dark:text-gray-600"
        style={{ backgroundImage: 'radial-gradient(circle, currentColor 1.2px, transparent 1.2px)', backgroundSize: '20px 20px' }} aria-hidden />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10 min-w-0">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8 sm:mb-12 px-1">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Everything your recruiting team needs</h2>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">One platform that replaces your CV inbox, spreadsheets, scheduling tools, and legacy ATS.</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-[2rem] md:rounded-[2.5rem] shadow-xl border border-gray-200 dark:border-gray-700 overflow-visible relative min-w-0">
          <div className="relative min-h-[280px] sm:min-h-[320px] md:min-h-[560px] lg:min-h-[620px] flex items-center justify-center py-10 sm:py-14 md:py-20 lg:py-24 px-4 sm:px-8 md:px-14 lg:px-20 overflow-visible">
            <svg className="absolute inset-0 w-full h-full pointer-events-none hidden md:block" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              <path d={RING_PATH} fill="none" stroke="#8B5CF6" strokeOpacity="0.5" strokeWidth="0.25" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="0.8 1.6" />
              <g transform={`translate(${ap.x}, ${ap.y}) rotate(${ap.angleDeg})`} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}>
                <path d="M 2.8 0 L -1.2 -1.6 L -0.4 0 L -1.2 1.6 Z" fill="#6C47FF" stroke="#5B3FFF" strokeWidth="0.2" strokeLinejoin="round" />
              </g>
            </svg>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-center max-w-[360px] px-4">
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">Hire faster.<br />Hire better.</h3>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mb-6">Set up in minutes. Your first AI interview can be live today.</p>
              <button type="button" onClick={onScheduleDemo}
                className="px-6 py-3 rounded-xl font-semibold text-sm text-white hover:opacity-90 transition-opacity"
                style={{ background: 'var(--brand-cta-bg)' }}>Schedule a Demo</button>
            </div>
            {B2B_OFFERINGS.map((item, index) => {
              const angleDeg = -90 + (index * 360) / N;
              const a = (angleDeg * Math.PI) / 180;
              const cx = 50 + RADIUS_PCT * Math.cos(a);
              const cy = 50 + RADIUS_PCT * Math.sin(a);
              const Icon = item.icon;
              return (
                <div key={item.title} className="absolute hidden md:block w-[100px] lg:w-[110px] -translate-x-1/2 -translate-y-1/2 z-[1]" style={{ left: `${cx}%`, top: `${cy}%` }}>
                  <div className="bg-white dark:bg-gray-700/95 rounded-2xl p-3 shadow-xl border border-gray-200/80 dark:border-gray-600">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${item.iconBg} ${item.iconColor}`}><Icon className="w-4 h-4" /></div>
                    <h4 className="text-[11px] lg:text-xs font-bold text-gray-900 dark:text-white mb-0.5 leading-tight">{item.title}</h4>
                    <p className="text-[9px] lg:text-[10px] text-gray-600 dark:text-gray-400 leading-tight line-clamp-2">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Mobile grid */}
          <div className="md:hidden px-6 pb-8">
            <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
              {B2B_OFFERINGS.map(item => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="bg-white dark:bg-gray-700/95 rounded-2xl p-3 shadow-md border border-gray-200 dark:border-gray-600">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${item.iconBg} ${item.iconColor}`}><Icon className="w-4 h-4" /></div>
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white mb-0.5">{item.title}</h4>
                    <p className="text-[10px] text-gray-600 dark:text-gray-400 leading-snug">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   FINAL CTA (mirrors FinalCTASimple exactly)
═══════════════════════════════════════════════ */
function useShapeColors(theme: string) {
  return theme === 'dark'
    ? { SHAPE: 'rgba(242,242,243,0.10)', SHAPE_SOFT: 'rgba(242,242,243,0.07)', SHAPE_LIGHT: 'rgba(242,242,243,0.05)' }
    : { SHAPE: 'rgba(167,139,250,0.35)', SHAPE_SOFT: 'rgba(196,181,253,0.28)', SHAPE_LIGHT: 'rgba(221,214,254,0.22)' };
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function AbstractShapes() {
  const { theme } = useTheme();
  const { SHAPE, SHAPE_SOFT, SHAPE_LIGHT } = useShapeColors(theme);
  return (
    <div className="relative w-full min-h-[200px] sm:min-h-[240px] flex items-center justify-center md:justify-end pr-0 md:pr-8 overflow-hidden">
      <motion.div className="absolute rounded-full pointer-events-none -left-[15%] -top-[10%]" style={{ width: '55%', paddingBottom: '55%', background: SHAPE, filter: 'blur(1px)' }} animate={{ scale: [1, 1.06, 1], opacity: [0.9, 1, 0.9] }} transition={{ duration: 5, repeat: Infinity, repeatType: 'reverse' }} aria-hidden />
      <motion.div className="absolute pointer-events-none" style={{ left: '20%', top: '24%', width: '22%', height: '22%', background: SHAPE_SOFT, clipPath: 'polygon(50% 0%,0% 100%,100% 100%)', filter: 'blur(0.5px)' }} animate={{ y: [0, -8, 0], rotate: [0, 5, 0], opacity: [0.85, 1, 0.85] }} transition={{ duration: 4.5, delay: 0.5, repeat: Infinity, repeatType: 'reverse' }} aria-hidden />
      <motion.div className="absolute rounded-full pointer-events-none" style={{ left: '28%', top: '52%', width: '18%', paddingBottom: '18%', background: SHAPE_LIGHT, filter: 'blur(0.5px)' }} animate={{ y: [0, 12, 0], x: [0, -6, 0], scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }} transition={{ duration: 5.5, delay: 1, repeat: Infinity, repeatType: 'reverse' }} aria-hidden />
      <motion.div className="absolute rounded-[22%] pointer-events-none" style={{ left: '48%', top: '22%', width: '20%', paddingBottom: '20%', background: SHAPE_SOFT, filter: 'blur(0.5px)' }} animate={{ y: [0, -10, 0], rotate: [0, 12, 0], scale: [1, 1.05, 1], opacity: [0.85, 1, 0.85] }} transition={{ duration: 4.8, delay: 0.3, repeat: Infinity, repeatType: 'reverse' }} aria-hidden />
      <motion.div className="absolute pointer-events-none" style={{ left: '8%', top: '62%', width: '24%', height: '28%', background: SHAPE_LIGHT, clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)', filter: 'blur(0.5px)' }} animate={{ y: [0, 8, 0], x: [0, 5, 0], rotate: [0, -8, 0], opacity: [0.75, 1, 0.75] }} transition={{ duration: 5.2, delay: 0.8, repeat: Infinity, repeatType: 'reverse' }} aria-hidden />
      <motion.div className="absolute rounded-full pointer-events-none" style={{ left: '62%', top: '58%', width: '12%', paddingBottom: '12%', background: SHAPE, filter: 'blur(0.5px)' }} animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }} transition={{ duration: 4.2, delay: 1.2, repeat: Infinity, repeatType: 'reverse' }} aria-hidden />
    </div>
  );
}
function B2BFinalCTA({ onScheduleDemo }: { onScheduleDemo: () => void }) {
  const { theme } = useTheme();
  const b2bLoginUrl = getB2BAppUrl();
  return (
    <section className="py-6 md:py-10 px-2 sm:px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="w-full rounded-2xl md:rounded-3xl overflow-hidden shadow-xl"
        style={{ background: 'var(--brand-cta-bg)' }}>
        <div className="flex flex-col md:flex-row md:items-center w-full max-w-[1600px] mx-auto">

          {/* Left: text + CTA */}
          <div className="flex-1 min-w-0 p-8 md:p-12 lg:p-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              Ready to hire smarter,<br className="hidden sm:block" /> not harder?
            </h2>
            <p className="text-white/85 text-base sm:text-lg mb-7 max-w-lg">
              Join 500+ companies using GoGrabJob for Business to screen candidates with AI, run automated interviews, and fill roles 40% faster.
            </p>
            <div className="flex flex-wrap gap-3 mb-5">
              <button
                type="button"
                onClick={onScheduleDemo}
                className="px-6 py-3 font-bold rounded-2xl shadow-lg hover:opacity-90 transition-opacity text-sm sm:text-base border-2"
                style={{ background: theme === 'dark' ? 'rgba(242,242,243,0.12)' : '#ffffff', color: theme === 'dark' ? '#fff' : '#7c3aed', borderColor: theme === 'dark' ? 'rgba(242,242,243,0.20)' : 'transparent' }}
              >
                Schedule a Demo
              </button>
              <a
                href={b2bLoginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 font-semibold rounded-2xl border-2 border-white/40 text-white hover:bg-white/10 transition-colors text-sm sm:text-base"
              >
                Start for Free →
              </a>
            </div>
            <p className="text-xs text-white/60">No credit card required · Free plan available · Live in 10 minutes</p>
          </div>

          {/* Right: illustration placeholder
              ── Illustration name: "undraw_hiring" ──
              Source : https://undraw.co/illustrations (search "hiring")
              Set the theme color to #5B3FFF to match the brand.
              Alt option: "undraw_team_work" or "undraw_people_search"
          */}
          <div className="w-full md:w-[45%] shrink-0 flex items-center justify-center p-8 md:p-10 min-h-[200px] md:min-h-[260px]">
            {/* ── Illustration to add here ──────────────────────────────
                Name   : undraw_hiring
                Source : https://undraw.co  →  search "hiring"
                Color  : set theme color to #ffffff (white) for this section
                Alt    : undraw_team_work · undraw_people_search
            ────────────────────────────────────────────────────────── */}
            <div className="w-full max-w-sm h-52 md:h-60 rounded-2xl flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/30"
              style={{ background: 'rgba(255,255,255,0.10)', backdropFilter: 'blur(8px)' }}>
              <Users className="w-10 h-10 text-white/50" />
              <div className="text-center px-4">
                <p className="text-sm font-semibold text-white/80">Recruiting Team Illustration</p>
                <p className="text-[11px] text-white/50 mt-1">undraw_hiring · undraw.co · color #ffffff</p>
              </div>
            </div>
          </div>

        </div>
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   SCHEDULE DEMO MODAL
═══════════════════════════════════════════════ */
interface DemoForm { name: string; email: string; company: string; phone: string }
function ScheduleDemoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState<DemoForm>({ name: '', email: '', company: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const s = encodeURIComponent(`Demo Request — ${form.company || form.name}`);
    const b = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nPhone: ${form.phone}\n\nI'd like to schedule a demo of GoGrabJob for Business.`);
    window.open(`mailto:sales@gograbjob.com?subject=${s}&body=${b}`, '_blank');
    setSubmitted(true);
  };
  const handleClose = () => { setSubmitted(false); setForm({ name: '', email: '', company: '', phone: '' }); onClose(); };
  if (!open) return null;
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200]" onClick={handleClose} aria-hidden />
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 16 }} transition={{ type: 'spring', duration: 0.4 }}
            className="fixed inset-0 z-[210] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="demo-title">
            <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
                <div>
                  <h2 id="demo-title" className="text-lg font-bold text-gray-900 dark:text-white">Schedule a Demo</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">We'll reach out within one business day.</p>
                </div>
                <button type="button" onClick={handleClose} className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors -mr-1" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>
              {submitted ? (
                <div className="px-6 py-10 flex flex-col items-center gap-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center"><Check className="w-7 h-7 text-violet-600 dark:text-violet-400" /></div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">Request sent!</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Thanks, {form.name.split(' ')[0]}. Our team will be in touch soon.</p>
                  </div>
                  <button type="button" onClick={handleClose} className="mt-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white hover:opacity-90 transition-opacity" style={{ background: 'var(--brand-cta-bg)' }}>Close</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                  {([
                    { id: 'name', label: 'Full name', icon: User, type: 'text', placeholder: 'Jane Smith', required: true, key: 'name' },
                    { id: 'email', label: 'Work email', icon: Mail, type: 'email', placeholder: 'jane@company.com', required: true, key: 'email' },
                    { id: 'company', label: 'Company name', icon: Building2, type: 'text', placeholder: 'Acme Corp', required: false, key: 'company' },
                    { id: 'phone', label: 'Phone number', icon: Phone, type: 'tel', placeholder: '+1 555 000 0000', required: false, key: 'phone' },
                  ] as const).map(f => (
                    <div key={f.id}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor={`demo-${f.id}`}>{f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}</label>
                      <div className="relative">
                        <f.icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input id={`demo-${f.id}`} required={f.required} type={f.type} placeholder={f.placeholder} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 transition" />
                      </div>
                    </div>
                  ))}
                  <button type="submit" className="w-full py-3 rounded-xl font-semibold text-sm text-white shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2" style={{ background: 'var(--brand-cta-bg)' }}>
                    <CalendarDays className="w-4 h-4" />Request Demo
                  </button>
                  <p className="text-xs text-center text-gray-400 dark:text-gray-500">By submitting you agree to our <a href="/privacy-policy" className="underline hover:text-violet-600">Privacy Policy</a>.</p>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
const BusinessPage: React.FC<BusinessPageProps> = ({ onNavigate }) => {
  const { theme, toggleTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);

  React.useEffect(() => {
    const fn = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const heroGradient = 'var(--brand-cta-bg)';

  const b2bLoginUrl = getB2BAppUrl();
  const scrollTo = (id: string) => { setMobileMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };
  const goHome = () => { if (onNavigate) onNavigate('home'); else window.location.href = '/'; };

  const navLinkCls = `min-h-[44px] flex items-center font-medium transition-colors touch-manipulation ${
    isScrolled ? 'text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400' : 'text-white hover:text-white/80'
  }`;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="GoGrabJob for Business — AI Resume Ops for Consultancies"
        description="Run resume optimization at scale for your placement team. Bulk credits, team dashboard, white-label options for consultancies."
        canonical="https://www.gograbjob.com/business"
      />

      {/* ════════════════════
          HERO (nav INSIDE the section — identical structure to DripfyStyledHero)
      ════════════════════ */}
      <section
        id="hero"
        className="relative pb-0 px-0 min-h-0 md:min-h-[72vh] flex flex-col overflow-visible rounded-xl sm:rounded-2xl md:rounded-2xl lg:rounded-3xl mx-2 xs:mx-3 sm:mx-4 md:mx-4 lg:mx-6 xl:mx-8 mt-2 sm:mt-4 md:mt-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] transition-colors duration-300"
        style={{ background: heroGradient }}
      >
        {/* ── Nav bar (inside hero, same as DripfyStyledHero) ── */}
        <nav
          className={`${isScrolled ? 'fixed top-0 left-0 right-0 z-[100] pt-[env(safe-area-inset-top)]' : 'relative'} w-full px-3 sm:px-6 lg:px-8 py-2 sm:py-4 transition-all duration-300 ${
            isScrolled ? 'bg-white dark:bg-gray-900 shadow-md border-b border-gray-200 dark:border-gray-700' : 'bg-transparent'
          }`}
          style={isScrolled ? {} : { maxWidth: 'inherit' }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 min-h-[52px] sm:h-16">
            {/* Logo + "Business" badge */}
            <button onClick={goHome} className="flex items-center gap-2 flex-shrink-0 min-h-[44px] hover:opacity-90 transition-opacity touch-manipulation">
              <GGJLogoFull iconSize={40} fontSize={20} textColor={isScrolled ? undefined : '#F2F2F3'} />
              <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide border ${
                isScrolled
                  ? 'text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/20'
                  : 'text-white/90 border-white/30 bg-white/10'
              }`}>
                Business
              </span>
            </button>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <button type="button" onClick={() => scrollTo('features')} className={navLinkCls}>Features</button>
              <button type="button" onClick={() => scrollTo('how-it-works')} className={navLinkCls}>How It Works</button>
              <button type="button" onClick={() => scrollTo('platform')} className={navLinkCls}>Platform</button>
              <button type="button" onClick={() => scrollTo('pricing')} className={navLinkCls}>Pricing</button>
              <button type="button" onClick={goHome}
                className={`min-h-[44px] flex items-center gap-1.5 font-semibold px-3 py-1.5 rounded-lg border transition-colors touch-manipulation ${
                  isScrolled ? 'text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20' : 'text-white border-white/40 hover:bg-white/10'
                }`}>
                <ArrowUpRight className="w-4 h-4 shrink-0" />For Job Seekers
              </button>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <button onClick={toggleTheme}
                className={`min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg transition-colors touch-manipulation ${isScrolled ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800' : 'text-white hover:bg-white/10'}`}
                aria-label="Toggle theme">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button type="button" onClick={() => setShowDemoModal(true)}
                className={`hidden lg:flex min-h-[44px] px-4 py-2 font-medium rounded-lg transition-colors touch-manipulation ${isScrolled ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800' : 'text-white hover:bg-white/10'}`}>
                Schedule Demo
              </button>
              <a href={b2bLoginUrl} target="_blank" rel="noopener noreferrer"
                className="hidden lg:flex min-h-[44px] px-4 sm:px-5 py-2.5 text-white font-semibold rounded-xl shadow-lg hover:opacity-90 transition-opacity touch-manipulation items-center justify-center gap-1.5"
                style={{ background: isScrolled ? 'var(--brand-cta-bg)' : 'rgba(255,255,255,0.2)', backdropFilter: isScrolled ? 'none' : 'blur(10px)' }}>
                <Briefcase className="w-4 h-4" />Login
              </a>
              <button type="button" onClick={() => setMobileMenuOpen(true)}
                className={`lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg transition-colors touch-manipulation ${isScrolled ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800' : 'text-white hover:bg-white/10'}`}
                aria-label="Open menu">
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] lg:hidden" onClick={() => setMobileMenuOpen(false)} aria-hidden />
              <motion.aside initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.25 }}
                className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white dark:bg-gray-800 shadow-xl z-[120] lg:hidden flex flex-col overflow-y-auto max-h-[100dvh] pt-[env(safe-area-inset-top)] pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <GGJLogoFull iconSize={32} fontSize={16} />
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold text-violet-600 dark:text-violet-400 border border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/20">Business</span>
                  </div>
                  <button type="button" onClick={() => setMobileMenuOpen(false)}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg touch-manipulation -mr-1" aria-label="Close menu">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <nav className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-0.5 min-h-0">
                  <button type="button" onClick={() => scrollTo('features')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium touch-manipulation w-full text-left">Features</button>
                  <button type="button" onClick={() => scrollTo('how-it-works')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium touch-manipulation w-full text-left">How It Works</button>
                  <button type="button" onClick={() => scrollTo('platform')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium touch-manipulation w-full text-left">Platform</button>
                  <button type="button" onClick={() => scrollTo('pricing')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium touch-manipulation w-full text-left">Pricing</button>
                  <button type="button" onClick={goHome} className="min-h-[44px] flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 touch-manipulation w-full text-left">
                    <ArrowUpRight className="w-4 h-4 shrink-0" />For Job Seekers
                  </button>
                </nav>
                <div className="px-2 py-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-1.5 flex-shrink-0">
                  <button type="button" onClick={() => { setMobileMenuOpen(false); setShowDemoModal(true); }}
                    className="w-full min-h-[44px] px-3 py-2.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors touch-manipulation">
                    Schedule Demo
                  </button>
                  <a href={b2bLoginUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}
                    className="w-full min-h-[44px] flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-xl text-white transition-opacity hover:opacity-90 touch-manipulation"
                    style={{ background: 'var(--brand-cta-bg)' }}>
                    <Briefcase className="w-4 h-4" />Login for Business<ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Decorative background curves (exact copy from DripfyStyledHero) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-[2rem] md:rounded-[2.5rem] bg-transparent">
          <svg className="absolute left-0 top-1/4 w-64 h-64 opacity-20" viewBox="0 0 200 200" aria-hidden>
            <path d="M10,50 Q50,10 90,50 T170,50" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
            <path d="M10,100 Q50,60 90,100 T170,100" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
          </svg>
          <svg className="absolute right-0 bottom-1/3 w-64 h-64 opacity-20" viewBox="0 0 200 200" aria-hidden>
            <path d="M30,50 Q70,90 110,50 T190,50" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
            <path d="M30,100 Q70,140 110,100 T190,100" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
          </svg>
          <svg className="absolute right-20 top-40 w-32 h-32 opacity-15" viewBox="0 0 100 100" aria-hidden>
            <circle cx="50" cy="50" r="30" stroke="white" strokeWidth="2" fill="none"/>
            <circle cx="50" cy="50" r="20" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>

        {/* Two-column layout (same structure as DripfyStyledHero) */}
        <div className="relative z-10 w-full max-w-7xl mx-auto md:max-w-none md:mx-0 px-5 sm:px-6 md:pl-8 lg:pl-10 md:pr-6 lg:pr-8 flex flex-col pt-4 md:pt-8 pb-24 sm:pb-28 md:pb-36 lg:pb-40">
          <div className="grid md:grid-cols-2 md:gap-3 lg:gap-5 items-center gap-4 sm:gap-5 overflow-visible">

            {/* LEFT: Copy */}
            <div className="text-left min-w-0 order-1 md:order-1 md:col-start-1 pr-0">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mb-3 sm:mb-4 md:mb-6">
                <div className="inline-flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/20">
                  <span className="text-white font-medium text-xs md:text-sm">AI-POWERED</span>
                  <svg width="4" height="4" viewBox="0 0 4 4" fill="none" className="text-white/50" aria-hidden><circle cx="2" cy="2" r="2" fill="currentColor"/></svg>
                  <span className="text-white font-medium text-xs md:text-sm">FREE TO START</span>
                </div>
              </motion.div>

              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-3 sm:mb-4 md:mb-6 leading-[1.15]" style={{ fontWeight: 800 }}>
                Hire Smarter.<br />Interview Faster.
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                className="text-sm sm:text-base md:text-lg text-white/90 mb-4 sm:mb-6 md:mb-8 leading-relaxed">
                GoGrabJob for Business gives your recruiting team AI-powered screening, automated video interviews, and a full ATS pipeline — set up in minutes, not weeks.
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
                <button type="button" onClick={() => setShowDemoModal(true)}
                  className="w-full sm:w-auto min-h-[44px] px-5 sm:px-8 py-3 sm:py-4 font-bold text-sm sm:text-base rounded-xl sm:rounded-2xl transition-colors shadow-xl flex items-center justify-center gap-2 touch-manipulation"
                  style={{ background: theme === 'dark' ? 'rgba(242,242,243,0.14)' : '#ffffff', color: theme === 'dark' ? 'var(--vscode-text-primary)' : '#7c3aed', border: theme === 'dark' ? '1px solid rgba(242,242,243,0.20)' : 'none' }}>
                  <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  Schedule a Demo
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                </button>
                <a href={b2bLoginUrl} target="_blank" rel="noopener noreferrer"
                  className="w-full sm:w-auto min-h-[44px] px-5 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white font-semibold text-sm sm:text-base rounded-xl sm:rounded-2xl hover:bg-white/20 transition-colors border border-white/20 touch-manipulation flex items-center justify-center gap-2">
                  <Briefcase className="w-4 h-4 shrink-0" />Login for Business
                </a>
              </motion.div>

              {/* Trust indicators (same as B2C) */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-wrap gap-2 sm:gap-4 text-white text-xs sm:text-sm">
                {['One-minute setup', '14-day free trial', 'No credit card'].map(t => (
                  <div key={t} className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
                      <path d="M7 10L9 12L13 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{t}</span>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* RIGHT: image-3-style mockup placeholder (matches HeroFlowSimulation column position) */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden md:flex justify-end overflow-visible min-w-0 order-2 md:order-2 md:col-start-2">
              <div className="w-full max-w-[26rem] lg:max-w-[30rem] xl:max-w-[34rem] ml-auto mr-0">
                <HeroMockupPlaceholder />
              </div>
            </motion.div>
          </div>
        </div>

      </section>

      {/* ════════════════════
          HOW IT WORKS (mirrors dark section from HomePage)
      ════════════════════ */}
      <section id="how-it-works" className="py-14 md:py-20 bg-[#1A1A1A] px-4 sm:px-6 lg:px-10 scroll-mt-20">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-violet-400 mb-3">How It Works</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">From posting to hired in 4 steps</h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto">No setup calls. No lengthy onboarding. Your first AI interview can go live today.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { num: '01', icon: FileText,   title: 'Post a Role',        desc: 'Create a job listing. Our AI automatically builds the interview questions and scoring rubric.' },
              { num: '02', icon: FileSearch, title: 'AI Screens CVs',     desc: 'Candidates apply. AI reads every CV, scores fit, and delivers a ranked shortlist instantly.' },
              { num: '03', icon: Video,      title: 'Launch AI Interviews', desc: 'Select shortlisted candidates and launch parallel AI video interviews — all at once.' },
              { num: '04', icon: BarChart3,  title: 'Review & Decide',    desc: 'Get scorecards, transcripts, and video recordings. Make fast, confident hiring decisions.' },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div key={step.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="relative flex flex-col gap-4">
                  {i < 3 && <div className="hidden lg:block absolute top-6 left-[calc(100%+0.75rem)] w-6"><ChevronRight className="w-4 h-4 text-violet-500 mx-auto" /></div>}
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-md shrink-0" style={{ background: 'var(--brand-cta-bg)' }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-violet-400 mb-1 block">{step.num}</span>
                    <h3 className="text-base font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* ── Illustration placeholder (non-hero) ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <IllustrationPlaceholder
              label="Workflow Illustration"
              sublabel="Add a flow diagram or product screenshot here"
              height="h-52"
              className="border-gray-600 dark:border-gray-600 bg-white/5"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="text-center mt-10">
            <button type="button" onClick={() => setShowDemoModal(true)}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-white shadow-lg hover:opacity-90 transition-opacity"
              style={{ background: 'var(--brand-cta-bg)' }}>
              <CalendarDays className="w-4 h-4" />See it in action — Schedule Demo<ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════  FEATURE TABS  ════════════════════ */}
      <B2BFeatureTabs b2bLoginUrl={b2bLoginUrl} />

      {/* ── Illustration placeholder between sections ── */}
      <section className="py-12 md:py-16 bg-white dark:bg-gray-950 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-center text-sm font-semibold uppercase tracking-widest text-violet-600 dark:text-violet-400 mb-4">In Action</p>
            <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-8">See GoGrabJob Business in action</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <IllustrationPlaceholder
                label="Dashboard Screenshot"
                sublabel="Add a screenshot of the recruiter dashboard"
                height="h-56"
                icon={<LayoutDashboard className="w-6 h-6 text-violet-400 dark:text-violet-500" />}
              />
              <IllustrationPlaceholder
                label="AI Interview View"
                sublabel="Add a screenshot of the AI interview interface"
                height="h-56"
                icon={<Video className="w-6 h-6 text-violet-400 dark:text-violet-500" />}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════  PLATFORM ORBITAL  ════════════════════ */}
      <B2BPlatformCenter onScheduleDemo={() => setShowDemoModal(true)} />

      {/* ════════════════════  FINAL CTA (mirrors FinalCTASimple)  ════════════════════ */}
      <B2BFinalCTA onScheduleDemo={() => setShowDemoModal(true)} />

      {/* ════════════════════  FOOTER  ════════════════════ */}
      <footer className="bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 py-8 px-4 sm:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <GGJLogoFull iconSize={32} fontSize={16} />
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold text-violet-600 dark:text-violet-400 border border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/20">Business</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <a href="/privacy-policy" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:text-gray-900 dark:hover:text-white transition-colors">Terms</a>
            <a href="/contact-us" className="hover:text-gray-900 dark:hover:text-white transition-colors">Contact</a>
            <button type="button" onClick={goHome} className="hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-1">
              <ArrowUpRight className="w-3.5 h-3.5" />For Job Seekers
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-600">&copy; {new Date().getFullYear()} GoGrabJob</p>
        </div>
      </footer>

      <ScheduleDemoModal open={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
};

export default BusinessPage;
